import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { IconButton } from '~/components/isolated/IconButton';
import { SimpleCounter } from '~/components/isolated/SimpleCounter';
import { useProductCard } from '~/components/ProductCard/hooks/useProductCard';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import type { Product } from '~/stores/CategoriesStore';
import {
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';
import { getApplicableMultibuyLevel } from '~/utils/multibuy';

interface SearchResultItemDropdownProps {
  product: Product;
  onClose: (e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
  inverse?: boolean;
}

export const SearchResultItemDropdown: React.FC<SearchResultItemDropdownProps> =
  observer(({ product, onClose, inverse = false }) => {
    const { handleClickProduct } = useCategoryAnalytics();

    const {
      currentItem,
      image,
      currentPrice,
      cartItemsCount,
      isFavorite,
      productHasManyVariants,
      isBundle,
      isMultiBuy,
      limit,
      toggleFavorite,
      changeCount,
      openSelectSKUModal,
    } = useProductCard(product);

    const handleProductClick = useCallback(() => {
      handleClickProduct(product);
      onClose();
    }, [product, handleClickProduct, onClose]);

    const addButtonHandler = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (productHasManyVariants && !isBundle) {
        onClose();
        openSelectSKUModal();
      } else {
        changeCount(cartItemsCount + 1, 'add');
      }
    };

    const handleToggleFavorite = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleFavorite();
    };

    const handleIncrement = () => {
      changeCount(cartItemsCount + 1, 'add');
    };

    const handleDecrement = () => {
      changeCount(cartItemsCount - 1, 'remove');
    };

    const handleCounterClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const displayPrice = useMemo(() => {
      let price: string | number = 0;

      if (!isMultiBuy) {
        price = currentPrice;
      } else {
        const applicableLevel = getApplicableMultibuyLevel(
          currentItem.multibuyPriceLevels,
          cartItemsCount,
        );

        price = applicableLevel
          ? convertPenceToPounds(applicableLevel.unitPrice)
          : currentPrice;
      }

      return formatPriceWithCurrency(price);
    }, [
      isMultiBuy,
      currentItem.multibuyPriceLevels,
      cartItemsCount,
      currentPrice,
    ]);

    return (
      <Link
        to={`/p/${product.slug}`}
        state={{ offerSku: currentItem.sku }}
        className={cn(
          'group flex h-[88px] cursor-default gap-3 rounded-[16px] bg-transparent p-2 no-underline transition-colors duration-200',
          inverse
            ? 'hover:bg-surface-inverse-transparent-2'
            : 'hover:bg-surface-transparent-2',
        )}
        onClick={handleProductClick}
      >
        <div
          className={cn(
            'relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] p-1 after:pointer-events-none after:absolute after:inset-0 after:rounded-[12px] after:transition-colors after:duration-200',
            inverse
              ? 'bg-surface-inverse-transparent-1 after:bg-surface-inverse-transparent-2 group-hover:after:bg-surface-inverse-transparent-3'
              : 'bg-surface-transparent-1 after:bg-surface-transparent-2 group-hover:after:bg-surface-transparent-3',
          )}
        >
          <CloudflareResponseImage
            src={currentItem.images[0] ?? image ?? ''}
            srcParams={{ width: 72, height: 72, fit: 'contain' }}
            alt={currentItem.name}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        </div>

        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h4
              className={cn(
                'm-0 overflow-hidden text-ellipsis whitespace-nowrap [font:var(--text-300-medium)]',
                inverse ? 'text-text-inverse-primary' : 'text-text-primary',
              )}
            >
              {currentItem.name}
            </h4>
            <span
              className={cn(
                '[font:var(--text-200-medium)]',
                inverse ? 'text-text-inverse-tertiary' : 'text-text-tertiary',
              )}
            >
              {displayPrice}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {cartItemsCount > 0 ? (
              <div onClick={handleCounterClick}>
                <SimpleCounter
                  count={cartItemsCount}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  variant="secondary"
                  colorVariant={inverse ? 'inverse' : 'default'}
                  className="h-12 w-[155px]!"
                  limit={limit}
                  hideTooltip
                />
              </div>
            ) : (
              <>
                <IconButton
                  className={cn([
                    'rounded-[8px]! border',
                    isFavorite && 'text-icon-brand',
                    inverse
                      ? 'border-stroke-inverse-primary! text-icon-inverse-primary! hover:bg-surface-inverse-transparent-2!'
                      : 'border-stroke-primary! text-icon-primary! hover:bg-surface-transparent-2!',
                  ])}
                  icon={isFavorite ? 'heart-fill' : 'heart'}
                  iconSize={20}
                  size="L"
                  variant="outline"
                  onClick={handleToggleFavorite}
                  aria-label="Toggle favorite"
                />
                <IconButton
                  className={cn(
                    'rounded-[8px]! border',
                    inverse
                      ? 'border-stroke-inverse-primary! text-icon-inverse-primary! hover:bg-surface-inverse-transparent-2!'
                      : 'border-stroke-primary! text-icon-primary! hover:bg-surface-transparent-2!',
                  )}
                  icon="plus-3.1"
                  iconSize={16}
                  size="L"
                  variant="outline"
                  onClick={addButtonHandler}
                  aria-label="Add to cart"
                />
              </>
            )}
          </div>
        </div>
      </Link>
    );
  });
